import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './ThePitch.module.css';

export default function QAndA() {
  const questions = useMemo(
    () => [
      'Have you ever met someone important… and later forgotten their details or why they mattered?',
      'Would it be useful to have one place where you keep the real context about people (not just a phone number)?',
      'Do you agree that “relationships” are one of the most valuable datasets in your life and business?',
      'Have you ever lost an opportunity because you didn’t follow up at the right time?',
      'Would you rather share your info with one scan than hand someone a business card that’s outdated tomorrow?',
      'Would it help if connecting with someone automatically saved the relationship in both of your systems?',
      'Do you want private-by-default sharing so families, teams, and communities can share safely without a social feed?',
    ],
    []
  );

  const [step, setStep] = useState(0);

  const isDone = step >= questions.length;

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Q &amp; A | MyPeopleDatabase</title>
        <meta
          name="description"
          content="A simple yes-ladder Q&A that builds agreement and reveals the MPD ID + private sharing solution."
        />
      </Helmet>

      <div className={styles.content}>
        <h1 className={styles.title}>Q &amp; A</h1>

        <div className={styles.hero}>
          <p className={styles.heroLead}>
            A few quick questions. If you keep saying “yes”, you’ll see the inevitable conclusion.
          </p>
          <p className={styles.heroSub}>
            This is designed to work in a conversation—no pressure, just agreement on reality.
          </p>
        </div>

        {!isDone ? (
          <div className={styles.card}>
            <div className={styles.cardKicker}>
              Question {step + 1} of {questions.length}
            </div>
            <div className={styles.cardQuestion}>{questions[step]}</div>
            <div className={styles.actionsRow}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => setStep(s => s + 1)}
              >
                Yes →
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setStep(questions.length)}
              >
                Skip to the reveal
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.revealCard}>
              <div className={styles.revealTitle}>The reveal</div>
              <p className={styles.revealLead}>
                MyPeopleDatabase is the system for relationship context—private by default—and MPD ID is the
                modern replacement for the business card.
              </p>
              <div className={styles.revealGrid}>
                <div className={styles.revealTile}>
                  <div className={styles.tileTitle}>MPD ID (link + QR)</div>
                  <div className={styles.tileBody}>One shareable identity page. One scan. Always up to date.</div>
                </div>
                <div className={styles.revealTile}>
                  <div className={styles.tileTitle}>The viral loop</div>
                  <div className={styles.tileBody}>
                    Scan → view → connect → both people save the relationship (with permissions).
                  </div>
                </div>
                <div className={styles.revealTile}>
                  <div className={styles.tileTitle}>Secure private groups</div>
                  <div className={styles.tileBody}>
                    Invite-only sharing for families, teams, cohorts, neighborhoods—without a social feed.
                  </div>
                </div>
              </div>

              <div className={styles.talkTrack}>
                <div className={styles.talkTrackTitle}>Talk track (say this out loud)</div>
                <ul className={styles.talkTrackList}>
                  <li className={styles.talkTrackItem}>
                    “Most people don’t have a contacts problem—they have a relationship context problem.”
                  </li>
                  <li className={styles.talkTrackItem}>
                    “MPD is where I keep the real story: who someone is, why they matter, and what I should do
                    next.”
                  </li>
                  <li className={styles.talkTrackItem}>
                    “MPD ID replaces the business card: one link and one QR code that’s always current.”
                  </li>
                  <li className={styles.talkTrackItem}>
                    “When we connect, it’s scan → view → connect—and it saves the relationship for both of us.”
                  </li>
                  <li className={styles.talkTrackItem}>
                    “And it’s private by default, so groups can share safely without turning into a social network.”
                  </li>
                </ul>
              </div>

              <div className={styles.ctaRow}>
                <Link to="/mpd-id" className={styles.primaryCta}>
                  See MPD ID →
                </Link>
                <Link to="/buy" className={styles.secondaryCta}>
                  Buy Now →
                </Link>
                <button type="button" className={styles.ghostCta} onClick={() => setStep(0)}>
                  Restart
                </button>
              </div>
            </div>

            <div className={styles.footerLinks}>
              <Link to="/the-pitch" className={styles.linkButton}>
                Read The Pitch →
              </Link>
              <Link to="/vision" className={styles.linkButton}>
                See the Vision →
              </Link>
              <Link to="/business-plan" className={styles.linkButton}>
                Business Plan →
              </Link>
              <Link to="/contact-us" className={styles.linkButton}>
                Talk to us →
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
